import React, { useEffect, useState } from "react";
import { makeStyles, useTheme } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import DialogContent from "@mui/material/DialogContent";
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import List from "@mui/material/List";
import ExpandFilter from "../../sharedComponents/smartSearch/ExpandFilter";
import ShowFilter from "../../sharedComponents/filter/ShowFilter";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  content: {
    background: theme.palette.background.paper,
    padding: 0,
    "&:first-child": {
      paddingTop: 0,
    },
  },
  chipsArea: {
    padding: theme.spacing(1),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 1.5),
  },
  applyButton: {
    borderRadius: "16px",
    color: "#fff",
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : theme.palette.primary.main,
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? theme.palette.background.paper
          : theme.palette.primary.dark,
    },
  },
  clearLink: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
  expansionPanel: {
    background: theme.palette.background.paper + " !important",
    "&::before": {
      opacity: "1 !important",
    },
    marginTop: "0 !important",
    boxShadow: "none",
  },
  expansionPanelSummary: {
    padding: "0 16px 0 10px",
    minHeight: "48px !important",
    margin: 0,
    "& > div:first-child": {
      margin: theme.spacing(1.5, 0),
    },
  },
  expansionPanelDetails: {
    padding: 0,
    color: theme.palette.text.primary,
  },
  expandIcon: {
    color: theme.palette.text.secondary,
  },
  filterTitle: {
    fontSize: "14px",
    fontWeight: "bold",
    color: theme.palette.text.primary,
  },
}));

const AccordionSection = ({ title, children, classes }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Accordion
      className={classes.expansionPanel}
      expanded={expanded}
      onChange={() => setExpanded((prev) => !prev)}
    >
      <AccordionSummary
        className={classes.expansionPanelSummary}
        expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
      >
        <Typography className={classes.filterTitle}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.expansionPanelDetails}>
        {expanded && <React.Fragment>{children}</React.Fragment>}
      </AccordionDetails>
    </Accordion>
  );
};

const hasActiveFilters = (filterState) =>
  filterState.selectedTitle.length > 0 ||
  filterState.selectedLeague.length > 0 ||
  filterState.selectedChampionship.length > 0 ||
  filterState.selectedSkill.length > 0 ||
  filterState.selectedTeams.length > 0 ||
  filterState.selectedNBATeams.length > 0 ||
  filterState.selectedWNBATeams.length > 0 ||
  filterState.selectedNBAGLeagueTeams.length > 0 ||
  filterState.selectedCurrentlyWithATeam.length > 0 ||
  filterState.selectedFormerCoach.length > 0 ||
  (filterState.selectedPExpereience && filterState.selectedPExpereience.length > 0);

export default function AdvancedFilterDialog(props) {
  const classes = useStyles();
  const theme = useTheme();
  const {
    open,
    onClose,
    onApply,
    onClearAll,
    filterState,
    onChecked,
    onChipDelete,
    filterTexts,
    onFilterTextChange,
    skills,
    league,
    positionTitleList,
    nbaTeamsList,
    WnbaTeamsList,
    nbaGLeagueTeamsList,
    getLeagueTextByValue,
  } = props;

  // Mirror SmartSearch's conditional rendering logic:
  // hideOtherFilter: true when no expType selected (shows Championship & Skills)
  // showCurrentPast: false when championship or skill selected (hides Current/Former coaches)
  const [hideOtherFilter, setHideOtherFilter] = useState(true);
  const [showCurrentPast, setShowCurrentPast] = useState(true);

  useEffect(() => {
    if (filterState.selectedExpType && filterState.selectedExpType.length > 0) {
      setHideOtherFilter(false);
    } else {
      setHideOtherFilter(true);
    }
  }, [filterState.selectedExpType]);

  useEffect(() => {
    if (
      (filterState.selectedChampionship && filterState.selectedChampionship.length > 0) ||
      (filterState.selectedSkill && filterState.selectedSkill.length > 0)
    ) {
      setShowCurrentPast(false);
    } else {
      setShowCurrentPast(true);
    }
  }, [filterState.selectedChampionship, filterState.selectedSkill]);

  const active = hasActiveFilters(filterState);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar className={classes.appBar} color="inherit">
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            Advanced Filters
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent className={classes.content}>
        {/* Active filter chips */}
        {active && (
          <div className={classes.chipsArea}>
            <ShowFilter list={filterState.selectedTitle} for="positionTitle" onDelete={onChipDelete} getLeagueTextByValue={getLeagueTextByValue} />
            <ShowFilter list={filterState.selectedLeague} for="league" onDelete={onChipDelete} getLeagueTextByValue={getLeagueTextByValue} />
            <ShowFilter list={filterState.selectedSkill} for="skill" onDelete={onChipDelete} getLeagueTextByValue={getLeagueTextByValue} />
            <ShowFilter list={filterState.selectedChampionship} for="championship" onDelete={onChipDelete} getLeagueTextByValue={getLeagueTextByValue} />
            <ShowFilter list={filterState.selectedNBATeams} for="nbaTeams" onDelete={onChipDelete} getLeagueTextByValue={getLeagueTextByValue} />
            <ShowFilter list={filterState.selectedWNBATeams} for="wnbaTeams" onDelete={onChipDelete} getLeagueTextByValue={getLeagueTextByValue} />
            <ShowFilter list={filterState.selectedNBAGLeagueTeams} for="nbaGLeagueTeams" onDelete={onChipDelete} getLeagueTextByValue={getLeagueTextByValue} />
            <ShowFilter list={filterState.selectedCurrentlyWithATeam} for="currentWork" onDelete={onChipDelete} getLeagueTextByValue={getLeagueTextByValue} />
            <ShowFilter list={filterState.selectedFormerCoach} for="formerCoaches" onDelete={onChipDelete} getLeagueTextByValue={getLeagueTextByValue} />
            <ShowFilter list={filterState.selectedPExpereience} for="playingExperience" onDelete={onChipDelete} getLeagueTextByValue={getLeagueTextByValue} />
          </div>
        )}

        {/* Action row: Clear All + Apply */}
        <div className={classes.actionRow}>
          {active ? (
            <Typography
              variant="caption"
              className={classes.clearLink}
              onClick={onClearAll}
            >
              Clear All
            </Typography>
          ) : (
            <span />
          )}
          <Button
            className={classes.applyButton}
            variant="contained"
            onClick={onApply}
            data-gaeventlabel="Search"
            data-gaeventcategory="Coach Profile Search"
            data-gaeventaction="Apply Filter Click"
          >
            Apply Filters
          </Button>
        </div>

        {/* Filter accordion sections */}
        {showCurrentPast && (
          <>
            <AccordionSection title="Current Coaches" classes={classes}>
              <List dense style={{ width: "100%" }}>
                <ExpandFilter
                  selectedItem={filterState.selectedCurrentlyWithATeam}
                  list={[{ Id: 1, Name: "NBA" }, { Id: 2, Name: "WNBA" }, { Id: 3, Name: "G League" }]}
                  Name="currentWork"
                  onChange={onChecked}
                  searchedText={filterTexts.currentWork}
                  onWithinFilterSearchedTextchange={(name, text) => onFilterTextChange("currentWork", text)}
                  getLeagueTextByValue={getLeagueTextByValue}
                />
              </List>
            </AccordionSection>

            <AccordionSection title="Former Coaches" classes={classes}>
              <List dense style={{ width: "100%" }}>
                <ExpandFilter
                  selectedItem={filterState.selectedFormerCoach}
                  list={[{ Id: 1, Name: "NBA" }, { Id: 2, Name: "WNBA" }, { Id: 3, Name: "G League" }]}
                  Name="formerCoaches"
                  onChange={onChecked}
                  searchedText={filterTexts.formerCoaches}
                  onWithinFilterSearchedTextchange={(name, text) => onFilterTextChange("formerCoaches", text)}
                  getLeagueTextByValue={getLeagueTextByValue}
                />
              </List>
            </AccordionSection>
          </>
        )}

        <AccordionSection title="NBA Teams" classes={classes}>
          <List dense style={{ width: "100%" }}>
            <ExpandFilter
              selectedItem={filterState.selectedNBATeams}
              list={nbaTeamsList}
              Name="nbaTeams"
              onChange={onChecked}
              searchedText={filterTexts.nbaTeams}
              onWithinFilterSearchedTextchange={(name, text) => onFilterTextChange("nbaTeams", text)}
              getLeagueTextByValue={getLeagueTextByValue}
            />
          </List>
        </AccordionSection>

        <AccordionSection title="WNBA Teams" classes={classes}>
          <List dense style={{ width: "100%" }}>
            <ExpandFilter
              selectedItem={filterState.selectedWNBATeams}
              list={WnbaTeamsList}
              Name="wnbaTeams"
              onChange={onChecked}
              searchedText={filterTexts.wnbaTeams}
              onWithinFilterSearchedTextchange={(name, text) => onFilterTextChange("wnbaTeams", text)}
              getLeagueTextByValue={getLeagueTextByValue}
            />
          </List>
        </AccordionSection>

        <AccordionSection title="NBA G League Teams" classes={classes}>
          <List dense style={{ width: "100%" }}>
            <ExpandFilter
              selectedItem={filterState.selectedNBAGLeagueTeams}
              list={nbaGLeagueTeamsList}
              Name="nbaGLeagueTeams"
              onChange={onChecked}
              searchedText={filterTexts.nbaGLeagueTeams}
              onWithinFilterSearchedTextchange={(name, text) => onFilterTextChange("nbaGLeagueTeams", text)}
              getLeagueTextByValue={getLeagueTextByValue}
            />
          </List>
        </AccordionSection>

        <AccordionSection title="Title" classes={classes}>
          <List dense style={{ width: "100%", margin: 0 }}>
            <ExpandFilter
              selectedItem={filterState.selectedTitle}
              list={positionTitleList}
              Name="positionTitle"
              onChange={onChecked}
              searchedText={filterTexts.title}
              onWithinFilterSearchedTextchange={(name, text) => onFilterTextChange("title", text)}
              getLeagueTextByValue={getLeagueTextByValue}
            />
          </List>
        </AccordionSection>

        {hideOtherFilter && (
          <>
            <AccordionSection title="Championship" classes={classes}>
              <List dense style={{ width: "100%" }}>
                <ExpandFilter
                  selectedItem={filterState.selectedChampionship}
                  list={league.filter((l) => l !== "na")}
                  Name="championship"
                  onChange={onChecked}
                  searchedText={filterTexts.championship}
                  onWithinFilterSearchedTextchange={(name, text) => onFilterTextChange("championship", text)}
                  getLeagueTextByValue={getLeagueTextByValue}
                />
              </List>
            </AccordionSection>

            <AccordionSection title="Skills" classes={classes}>
              <List dense style={{ width: "100%" }}>
                <ExpandFilter
                  selectedItem={filterState.selectedSkill}
                  list={skills.filter((skill) => skill.includeUnderSearchFilter)}
                  Name="skill"
                  onChange={onChecked}
                  searchedText={filterTexts.skill}
                  onWithinFilterSearchedTextchange={(name, text) => onFilterTextChange("skill", text)}
                  getLeagueTextByValue={getLeagueTextByValue}
                />
              </List>
            </AccordionSection>
          </>
        )}

        <AccordionSection title="Playing Experience" classes={classes}>
          <List dense style={{ width: "100%" }}>
            <ExpandFilter
              selectedItem={filterState.selectedPExpereience}
              list={[
                { Id: 1, Name: "NBA" },
                { Id: 2, Name: "WNBA" },
                { Id: 3, Name: "G League" },
                { Id: 4, Name: "Other" },
              ]}
              Name="playingExperience"
              onChange={onChecked}
              searchedText={filterTexts.pExperience}
              onWithinFilterSearchedTextchange={(name, text) => onFilterTextChange("pExperience", text)}
              getLeagueTextByValue={getLeagueTextByValue}
            />
          </List>
        </AccordionSection>
      </DialogContent>
    </Dialog>
  );
}
